import { Badge } from "@/components/ui/badge";

export const STATUS_META = {
  draft: { label: "Draft", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  assigned: { label: "Assigned", cls: "bg-blue-400/10 text-blue-400 border-blue-400/20" },
  acknowledged: { label: "Acknowledged", cls: "bg-cyan-400/10 text-cyan-400 border-cyan-400/20" },
  in_progress: { label: "In Progress", cls: "bg-emerald-600/10 text-emerald-400 border-emerald-600/20" },
  submitted: { label: "Submitted", cls: "bg-violet-400/10 text-violet-400 border-violet-400/20" },
  approved: { label: "Approved", cls: "bg-emerald-400/10 text-emerald-400 border-emerald-400/20" },
  completed: { label: "Completed", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  not_started: { label: "Not Started", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  waiting_information: { label: "Waiting Info", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  waiting_material: { label: "Waiting Material", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  waiting_customer: { label: "Waiting Customer", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  blocked: { label: "Blocked", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  on_hold: { label: "On Hold", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  extension_requested: { label: "Extension Req", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  reopened: { label: "Reopened", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20" },
  overdue: { label: "Overdue", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export const PRIORITY_META = {
  low: { label: "Low", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  normal: { label: "Normal", cls: "bg-blue-400/10 text-blue-400 border-blue-400/20" },
  high: { label: "High", cls: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  urgent: { label: "Urgent", cls: "bg-emerald-600/10 text-emerald-400 border-emerald-600/20" },
  critical: { label: "Critical", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <Badge variant="outline" className={`rounded-sm font-medium ${m.cls}`} data-testid={`status-${status}`}>
      {m.label}
    </Badge>
  );
}

export function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.normal;
  return (
    <Badge variant="outline" className={`rounded-sm font-medium ${m.cls}`}>
      {m.label}
    </Badge>
  );
}

export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function fmtDateShort(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// ============ "Control Center" design system — shared across every list/dashboard page ============

// Dark emerald-gradient hero banner used at the top of every major list/dashboard page.
export function HeroBanner({ eyebrow, title, subtitle, right, testId }) {
  return (
    <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-emerald-950 via-teal-900 to-slate-900 border border-emerald-700/50 px-6 py-5 mb-6 shadow-lg" data-testid={testId || "hero-banner"}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          {eyebrow && (
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-2.5 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {eyebrow}
            </div>
          )}
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{title}</h1>
          {subtitle && <p className="text-sm text-emerald-100/70 mt-1">{subtitle}</p>}
        </div>
        {right && <div className="flex items-center gap-2 flex-wrap">{right}</div>}
      </div>
    </div>
  );
}

// Bright white KPI card with a colored top accent bar — used in the summary-stat rows under a hero banner.
const KPI_ACCENTS = {
  emerald: "bg-emerald-500", blue: "bg-blue-500", amber: "bg-amber-500", rose: "bg-rose-500",
  violet: "bg-violet-500", indigo: "bg-indigo-500", cyan: "bg-cyan-500", zinc: "bg-zinc-400",
};
export function KPICard({ label, value, icon: Icon, accent = "emerald", onClick, testId, subtext }) {
  const El = onClick ? "button" : "div";
  return (
    <El onClick={onClick} data-testid={testId} className={`bg-white rounded-md overflow-hidden text-left shadow-sm ${onClick ? "hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer" : ""}`}>
      <div className={`h-1 w-full ${KPI_ACCENTS[accent] || KPI_ACCENTS.emerald}`} />
      <div className="p-3.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">{label}</span>
          {Icon && <Icon size={16} className="text-slate-400 opacity-80 group-hover:opacity-100" />}
        </div>
        <div className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
        {subtext && <div className="text-[11px] text-slate-400 mt-0.5">{subtext}</div>}
      </div>
    </El>
  );
}

// Light, high-density status pill for data-grid tables (as opposed to the dark translucent StatusBadge above).
const GRID_PILL = {
  neutral: "bg-slate-100 text-slate-700 border-slate-300",
  info: "bg-blue-100 text-blue-800 border-blue-300",
  success: "bg-emerald-100 text-emerald-800 border-emerald-300",
  warning: "bg-amber-100 text-amber-800 border-amber-300",
  danger: "bg-rose-100 text-rose-800 border-rose-300",
  progress: "bg-cyan-100 text-cyan-800 border-cyan-300",
};
export function GridPill({ tone = "neutral", children, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${GRID_PILL[tone]}`}>
      {children}
    </span>
  );
}

